/* eslint-disable sonarjs/no-duplicate-string */
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional, Renderer2, RendererFactory2, ViewEncapsulation } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { WINDOW } from '@wizbii/angular-utilities';
import { Request } from 'express';

// tslint:disable: no-duplicate-string

@Injectable({ providedIn: 'root' })
export class CustomMetaService {
  private readonly renderer: Renderer2;

  private readonly defaultTitle = "Toutes les solutions pour l'avenir des jeunes";
  private readonly defaultDescription = 'Emploi, formation, accompagnement';
  private readonly defaultRobots = 'index,follow';
  private readonly defaultImgUrlBig = 'https://storage.googleapis.com/wizbii/static-assets-v4/1jeune1solution-new.png';
  private readonly defaultImgUrlSmall =
    'https://storage.googleapis.com/wizbii/static-assets-v4/1jeune1solution-small-new.png';
  private readonly defaultImageAlt = '1jeune1solution';

  constructor(
    private readonly metaService: Meta,
    private readonly titleService: Title,
    private readonly rendererFactory: RendererFactory2,
    @Inject(WINDOW) private readonly window: any,
    @Inject(DOCUMENT) private readonly document: any,
    @Optional() @Inject(REQUEST) private readonly request: Request
  ) {
    this.renderer = this.rendererFactory.createRenderer(document, {
      id: Date.now().toString(),
      encapsulation: ViewEncapsulation.None,
      styles: [],
      data: {},
    });
  }

  updateForSeo({
    title,
    description,
    robotValue,
    imgSmall,
    imgBig,
    altImg,
  }: {
    title?: string;
    description?: string;
    robotValue?: string;
    imgSmall?: string | null;
    imgBig?: string | null;
    altImg?: string;
  }): void {
    this.updateTitle(title ?? this.defaultTitle);
    this.updateDescription(description ?? this.defaultDescription);
    this.updateRobots(robotValue ?? this.defaultRobots);
    this.updateImg(
      imgSmall ?? this.defaultImgUrlSmall,
      imgBig ?? this.defaultImgUrlBig,
      altImg ?? this.defaultImageAlt
    );
  }

  restoreForSeo(): void {
    this.restoreTitle();
    this.restoreDescription();
    this.restoreRobots();
    this.restoreImg();
  }

  private updateTitle(title: string): void {
    this.titleService.setTitle(title);
    this.updateOrAddTags([
      { property: 'og:title', content: title },
      { name: 'title', content: title },
    ]);
  }

  private restoreTitle(): void {
    this.titleService.setTitle(this.defaultTitle);
    this.updateOrAddTags([
      { property: 'og:title', content: this.defaultTitle },
      { name: 'title', content: this.defaultTitle },
    ]);
  }

  private updateRobots(value: string): void {
    this.updateOrAddTag({ name: 'robots', content: value });
  }

  private restoreRobots(): void {
    this.updateOrAddTag({ name: 'robots', content: this.defaultRobots });
  }

  private updateDescription(description: string): void {
    this.updateOrAddTags([
      { name: 'description', content: description },
      { property: 'og:description', content: description },
    ]);
  }

  private restoreDescription(): void {
    this.updateOrAddTags([
      { name: 'description', content: this.defaultDescription },
      { property: 'og:description', content: this.defaultDescription },
    ]);
  }

  private updateImg(imgSmall: string, imgBig: string, altImg: string): void {
    this.updateOrAddTags([
      { id: 'og:image-1', property: 'og:image', content: imgBig },
      { id: 'og:image:secure_url-1', property: 'og:image:secure_url', content: imgBig },
      { id: 'og:image:alt-1', property: 'og:image:alt', content: altImg },
      { id: 'og:image-2', property: 'og:image', content: imgSmall },
      { id: 'og:image:secure_url-2', property: 'og:image:secure_url', content: imgSmall },
      { id: 'og:image:alt-2', property: 'og:image:alt', content: altImg },
      { id: 'meta:image-1', itemprop: 'image', content: imgBig },
      { id: 'meta:image-2', itemprop: 'image', content: imgSmall },
    ]);
  }

  private restoreImg(): void {
    this.updateOrAddTags([
      { id: 'og:image-1', property: 'og:image', content: this.defaultImgUrlBig },
      { id: 'og:image:secure_url-1', property: 'og:image:secure_url', content: this.defaultImgUrlBig },
      { id: 'og:image:alt-1', property: 'og:image:alt', content: this.defaultImageAlt },
      { id: 'og:image-2', property: 'og:image', content: this.defaultImgUrlSmall },
      { id: 'og:image:secure_url-2', property: 'og:image:secure_url', content: this.defaultImgUrlSmall },
      { id: 'og:image:alt-2', property: 'og:image:alt', content: this.defaultImageAlt },
      { id: 'meta:image-1', itemprop: 'image', content: this.defaultImgUrlBig },
      { id: 'meta:image-2', itemprop: 'image', content: this.defaultImgUrlSmall },
    ]);
  }

  updateOrAddTag(meta: MetaDefinition): void {
    if (!meta) {
      return;
    }

    const id = Object.keys(meta)[0];

    if (Object.keys(meta).includes('id') && this.metaService.getTag(`${id}="${meta[id]}"`)) {
      this.metaService.updateTag(meta, `${id}="${meta[id]}"`);
      return;
    }

    const key = Object.keys(meta)[0];
    if (this.metaService.getTag(`${key}="${meta[key]}"`)) {
      this.metaService.updateTag(meta, `${key}="${meta[key]}"`);
      return;
    }

    this.metaService.addTag(meta);
  }

  updateOrAddTags(metas: MetaDefinition[]): void {
    if (!metas) {
      return;
    }

    metas.forEach((meta) => {
      this.updateOrAddTag(meta);
    });
  }

  deleteTag(meta: MetaDefinition): void {
    if (!meta) {
      return;
    }

    const key = Object.keys(meta)[0];

    this.metaService.removeTag(`${key}="${meta[key]}"`);
  }

  deleteTags(metas: MetaDefinition[]): void {
    if (!metas) {
      return;
    }

    return metas.forEach((meta) => {
      this.deleteTag(meta);
    });
  }

  updateOrAddCanonicalURL(url?: string): void {
    const canUrl = url
      ? url
      : this.request
      ? [this.request.protocol, '//', this.request.hostname, this.request.path].join('')
      : [this.window.location.protocol, '//', this.window.location.host, this.window.location.pathname].join('');

    if (!this.document.getElementById('canonical-url')) {
      const link = this.renderer.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canUrl);
      link.setAttribute('id', 'canonical-url');
      this.renderer.appendChild(this.document.head, link);

      return;
    }

    this.document.getElementById('canonical-url').setAttribute('href', canUrl);
  }

  deleteCanonicalURL(): void {
    const link = this.document.getElementById('canonical-url');
    if (!link) {
      return;
    }

    this.renderer.removeChild(this.document.head, link);
  }
}
